import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { useAnalyticsConfig, useUpsertAnalyticsConfig } from '../hooks/useIntelligenceReports';
import {
  SOURCE_CONFIG_FIELD,
  SOURCE_CONFIG_LABELS,
  SOURCE_LABELS,
  THRESHOLD_FIELDS,
  type AnalyticsSourceKey,
  type UpsertAnalyticsConfigPayload,
} from '../types';

const SOURCES: AnalyticsSourceKey[] = ['ga4', 'clarity', 'sentry', 'meta'];

const asText = (value: string | number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

/**
 * Per-merchant analytics configuration: the four property IDs that decide which
 * sources can be aggregated, plus the thresholds that tune the rule-based
 * writer's flagging.
 *
 * Two things worth knowing, both surfaced in the UI rather than left implicit:
 *  - Saving a config is what opts a merchant INTO the monthly cadence. The
 *    scheduler iterates analytics-config rows, so a merchant without one is
 *    never auto-reported on.
 *  - These IDs are separate from the merchant's storefront analytics settings.
 *    Setting a Clarity project here does not start collection on the storefront,
 *    and vice versa — this record only tells the report where to READ from.
 */
export function AnalyticsConfigModal({
  isOpen,
  onClose,
  merchantId,
  merchantName,
  canManage,
}: {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
  merchantName?: string;
  canManage: boolean;
}) {
  const { data: config, isLoading } = useAnalyticsConfig(isOpen ? merchantId : undefined);
  const upsert = useUpsertAnalyticsConfig();

  // Held as strings so an emptied field is distinguishable from a zero, and so
  // the number inputs don't fight the user mid-edit.
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!config) return;
    const next: Record<string, string> = {};
    SOURCES.forEach((s) => {
      next[SOURCE_CONFIG_FIELD[s]] = asText(config[SOURCE_CONFIG_FIELD[s]]);
    });
    THRESHOLD_FIELDS.forEach((t) => {
      next[t.field] = asText(config[t.field as keyof typeof config] as number | null);
    });
    setValues(next);
  }, [config]);

  const set = (field: string, value: string) => setValues((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    const payload: UpsertAnalyticsConfigPayload = {};

    SOURCES.forEach((s) => {
      const field = SOURCE_CONFIG_FIELD[s] as keyof UpsertAnalyticsConfigPayload;
      const raw = (values[field] ?? '').trim();
      // Send an explicit null to clear an id; the backend only skips a field
      // when it is `undefined`, so null is how "disconnect this source" travels.
      (payload as Record<string, unknown>)[field] = raw === '' ? null : raw;
    });

    THRESHOLD_FIELDS.forEach((t) => {
      const raw = (values[t.field] ?? '').trim();
      if (raw === '') {
        (payload as Record<string, unknown>)[t.field] = null;
        return;
      }
      const parsed = Number(raw);
      // A non-numeric leftover would be rejected by class-validator anyway;
      // dropping it keeps the rest of the save from failing on one bad field.
      if (Number.isFinite(parsed)) (payload as Record<string, unknown>)[t.field] = parsed;
    });

    await upsert.mutateAsync({ merchantId, payload });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6 max-h-[80vh] overflow-y-auto">
        <h2 className="text-[18px] font-bold text-foreground">Analytics configuration</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          {merchantName ? `For ${merchantName}. ` : ''}
          Tells the monthly report where to read each source from, and when to flag a metric.
        </p>

        {isLoading ? (
          <div className="py-12 text-center text-muted-foreground">Loading configuration…</div>
        ) : (
          <>
            <div className="mt-5 rounded-[10px] border border-border bg-muted/40 px-4 py-3 text-[12px] text-muted-foreground">
              Saving this configuration also opts the merchant into the scheduled monthly run — the
              batch job only covers merchants that have one. These IDs point the report at existing
              analytics properties; they do not start collection on the storefront.
            </div>

            <h3 className="mt-5 text-[14px] font-semibold text-foreground">Source IDs</h3>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {SOURCES.map((source) => {
                const field = SOURCE_CONFIG_FIELD[source];
                return (
                  <label key={source} className="block">
                    <span className="text-[13px] font-medium text-foreground">
                      {SOURCE_LABELS[source]}
                    </span>
                    <input
                      type="text"
                      value={values[field] ?? ''}
                      onChange={(e) => set(field, e.target.value)}
                      disabled={!canManage}
                      placeholder={SOURCE_CONFIG_LABELS[source]}
                      className="mt-1 h-[40px] w-full rounded-[8px] border border-border bg-background px-3 text-sm text-foreground disabled:opacity-50"
                    />
                    <span className="mt-1 block text-[12px] text-muted-foreground">
                      Leave blank to leave this source disconnected.
                    </span>
                  </label>
                );
              })}
            </div>

            <h3 className="mt-6 text-[14px] font-semibold text-foreground">Flagging thresholds</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Rates are fractions between 0 and 1 — 0.55 means 55%. Blank uses the platform default
              shown beneath each field.
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {THRESHOLD_FIELDS.map((t) => (
                <label key={t.field} className="block">
                  <span className="text-[13px] font-medium text-foreground">{t.label}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step={t.kind === 'rate' ? 0.01 : 1}
                    min={0}
                    value={values[t.field] ?? ''}
                    onChange={(e) => set(t.field, e.target.value)}
                    disabled={!canManage}
                    placeholder={String(t.defaultValue)}
                    className="mt-1 h-[40px] w-full rounded-[8px] border border-border bg-background px-3 text-sm text-foreground disabled:opacity-50"
                  />
                  <span className="mt-1 block text-[12px] text-muted-foreground">
                    {t.hint} · default {t.defaultValue}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" size="sm" className="h-[40px]" onClick={onClose}>
            {canManage ? 'Cancel' : 'Close'}
          </Button>
          {canManage && (
            <Button
              size="sm"
              className="h-[40px]"
              disabled={isLoading || upsert.isPending}
              loading={upsert.isPending}
              onClick={handleSave}
            >
              Save configuration
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
